/**
 * SortControl — the directory results "Sort" dropdown (TypeScript per
 * UX-13 S1, markup unchanged — the forest pill stays per the Phase 1
 * direction §8: it is the band's one dark anchor).
 *
 * Restores the prototype's (directory.jsx) sort affordance, which the peace-os
 * port had dropped. Single-select, modelled on the prototype's `Dropdown`:
 * a forest pill (sliders icon + current label + chevron) that opens a floating
 * radio menu, with click-outside + Escape to close. Shown on BOTH the orgs and
 * people views; the ordering itself lives in useUnifiedSearch (reorderBySort).
 *
 * Options mirror DIR_SORTS exactly:
 *   - 'recommended' → "Recommended first" (default; recommended-first then A-Z)
 *   - 'alpha'       → "Name (A-Z)"
 *   - 'county'      → "County"
 * (Individuals carry no recommended flag or county, so those two options
 * gracefully collapse to A-Z for the people view.)
 */

import { useState, useRef, useEffect, type ReactNode } from 'react'
import { ChevronDown, SlidersHorizontal, Check } from 'lucide-react'

export type SortMode = 'recommended' | 'alpha' | 'county'

export const SORT_OPTIONS: ReadonlyArray<{ value: SortMode; label: string }> = [
  { value: 'recommended', label: 'Recommended first' },
  { value: 'alpha', label: 'Name (A–Z)' },
  { value: 'county', label: 'County' },
]

export interface SortControlProps {
  value: string
  onChange: (value: SortMode) => void
}

export function SortControl({ value, onChange }: SortControlProps): ReactNode {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const current = SORT_OPTIONS.find((o) => o.value === value) ?? SORT_OPTIONS[0]

  return (
    <div ref={ref} className="relative min-w-0 max-w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Sort: ${current.label}`}
        className="inline-flex max-w-full items-center gap-2 rounded-pill border border-sage-300 bg-paper px-3.5 py-[7px] text-[13px] font-medium text-ink-soft transition-all duration-[var(--dur-base)] ease-quiet hover:border-sage-400 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <SlidersHorizontal size={14} aria-hidden="true" className="text-sage-400" />
        {current.label}
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={`opacity-70 transition-transform duration-[var(--dur-base)] ease-quiet ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Sort by"
          className="absolute right-0 top-[44px] z-40 min-w-[210px] rounded-[14px] border border-sage-800/[8%] bg-paper p-1.5 shadow-sh3"
        >
          {SORT_OPTIONS.map((o) => {
            const on = o.value === value
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => {
                  onChange(o.value)
                  setOpen(false)
                }}
                className={`flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left text-[13.5px] transition-colors ${
                  on ? 'bg-sage-800/[6%] font-semibold text-ink' : 'font-medium text-ink hover:bg-sand'
                }`}
              >
                <span
                  className={`flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full ${
                    on ? 'bg-sage-800 text-cream' : 'border-[1.5px] border-sage-300'
                  }`}
                >
                  {on && <Check size={11} strokeWidth={2.4} aria-hidden="true" />}
                </span>
                <span className="flex-1">{o.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
